/**
 * @file
 * Behaviours for views exposed-filter forms.
 *
 * The library is attached from the views-view template, so these enhancements
 * apply to the exposed filters rendered within views:
 * - Close any open <details> (e.g. filter dropdowns) on outside interaction.
 * - Date fields render as text (showing their placeholder) until focused.
 * - Redirect Tab focus between the "Sort by" select and the "Clear filters"
 *   button when active filters are shown, without reordering the DOM.
 * - Announce the new result count and restore focus after an AJAX update.
 */

((Drupal, once) => {
  Drupal.behaviors.nipuExposedFilters = {
    attach (context) {
      this.closeDetailsOnOutsideClick(context);
      this.placeholderDateInputs(context);
      this.announceAjaxResults(context);
    },

    /**
     * Announces refreshed results and moves focus into them after AJAX.
     *
     * Views answers an exposed-filter submit or a pager click with a
     * ReplaceCommand on `.js-view-dom-id-*`, so the whole view wrapper is
     * swapped out: the element that had focus is destroyed (focus falls back
     * to <body>, WCAG 2.4.3) and nothing tells assistive tech that the
     * results changed (WCAG 4.1.3). A live region inside the template cannot
     * cover the second part — it would be re-created along with everything
     * else and never announced — so announce through Drupal's persistent
     * region instead.
     *
     * On a replacement, attachBehaviors() receives the new view element as
     * context; on initial page load context is the document.
     */
    announceAjaxResults (context) {
      if (context.nodeType !== Node.ELEMENT_NODE || !context.matches('.view')) {
        return;
      }

      const results = context.querySelector('[data-search-results]');

      if (!results) {
        return;
      }

      // Focus first: Drupal.announce() is debounced by 200ms, so the count is
      // spoken after the region's own name rather than being cut off by it.
      results.focus();

      // Prefer the display's own summary ("Showing @total result(s)") or its
      // empty text; displays that render neither fall back to a row count.
      const summary = context.querySelector('.views-results, .view-empty');
      const message = summary ? summary.textContent.trim()
        || Drupal.formatPlural(
          results.querySelectorAll('.views-row').length,
          '1 result',
          '@count results',
        ) : null;

      Drupal.announce(message);
    },

    /**
     * Closes open <details> when a click/focus happens outside of it.
     */
    closeDetailsOnOutsideClick (context) {
      const detailsEls = once('exposed-filters-details', 'details', context);

      if (!detailsEls.length) {
        return;
      }

      // Flag the details elements handled by this behavior.
      detailsEls.forEach((details) => {
        details.classList.add('js-details-close');
      });

      // Bind a single outside-interaction listener for the whole page.
      once('exposed-filters-details-listener', 'html').forEach((html) => {
        const closeOpenDetails = (event) => {
          document
            .querySelectorAll('details[open].js-details-close')
            .forEach((details) => {
              if (!details.contains(event.target)) {
                details.removeAttribute('open');
              }
            });
        };

        html.addEventListener('click', closeOpenDetails);
        html.addEventListener('focusin', closeOpenDetails);
        html.addEventListener('keyup', (event) => {
          if (event.key === 'Escape') {
            closeOpenDetails(event);
          }
        });
      });
    },

    /**
     * Date fields: show as text with label placeholder, switch to date on focus.
     */
    placeholderDateInputs (context) {
      const dateInputs = once('exposed-filters-date', '.js-form-type-date input', context);

      dateInputs.forEach((input) => {
        const placeholderText = input.getAttribute('placeholder') || '';

        // Switch to text initially if no value is set.
        if (!input.value) {
          input.type = 'text';
        }

        input.addEventListener('focus', () => {
          input.type = 'date';
        });

        input.addEventListener('mouseover', () => {
          input.type = 'date';
        });

        input.addEventListener('mouseout', () => {
          if (!input.value && document.activeElement !== input) {
            input.type = 'text';
            input.placeholder = placeholderText;
          }
        });

        input.addEventListener('blur', () => {
          if (!input.value) {
            input.type = 'text';
            input.placeholder = placeholderText;
          }
        });
      });
    },
  };
})(Drupal, once);
